import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Bell,
  AlertTriangle,
  Shield,
  Server,
  User,
  TestTubeDiagonal,
  Filter,
  SortAsc,
  SortDesc,
  Check,
  Trash2,
  Eye,
} from 'lucide-react';
import {
  getNotifications,
  getUnreadCount,
  updateNotificationStatus,
  deleteNotification,
  type NotificationItem,
} from '../api-utils/notifications';

interface NotificationsPageProps {
  onBack?: () => void;
  embedded?: boolean;
}

type NotificationType = 'threat' | 'remediation' | 'tests' | 'system' | 'user';

function toDisplayType(type?: string): NotificationType {
  const t = (type || '').toLowerCase();
  if (t === 'threat') return 'threat';
  if (t === 'remediation') return 'remediation';
  if (t === 'test' || t === 'tests') return 'tests';
  if (t === 'user') return 'user';
  return 'system';
}

const NotificationsPage: React.FC<NotificationsPageProps> = ({ onBack, embedded }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<NotificationType | 'all'>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      const items = await getNotifications();
      setNotifications(items);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const getTypeIcon = (type: NotificationType) => {
    switch (type) {
      case 'threat':
        return <AlertTriangle className="w-5 h-5" />;
      case 'remediation':
        return <Shield className="w-5 h-5" />;
      case 'system':
        return <Server className="w-5 h-5" />;
      case 'tests':
        return <TestTubeDiagonal className="w-5 h-5" />;
      case 'user':
        return <User className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case 'threat':
        return 'text-crit bg-crit/15';
      case 'remediation':
        return 'text-done bg-done/15';
      case 'system':
        return 'text-prog bg-prog/15';
      case 'tests':
        return 'text-need bg-need/15';
      case 'user':
        return 'text-acc-t bg-acc/15';
    }
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '—';
    const date = new Date(timeStr);
    if (isNaN(date.getTime())) return timeStr;
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const formatFullTime = (timeStr?: string) => {
    if (!timeStr) return '—';
    const date = new Date(timeStr);
    if (isNaN(date.getTime())) return timeStr;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const markAsRead = async (id: string) => {
    try {
      const ok = await updateNotificationStatus(id, 'read');
      if (ok) await load();
    } catch {
      // keep UI as is
    }
  };

  const markAllAsRead = async () => {
    const unread = notifications.filter((n) => n.status === 'unread');
    for (const n of unread) {
      try {
        await updateNotificationStatus(n._id, 'read');
      } catch {
        // continue
      }
    }
    await load();
  };

  const handleDeleteNotification = async (id: string) => {
    try {
      const ok = await deleteNotification(id);
      if (ok) await load();
    } catch {
      // keep UI as is
    }
  };

  const clearAllRead = async () => {
    const read = notifications.filter((n) => n.status === 'read');
    for (const n of read) {
      try {
        await deleteNotification(n._id);
      } catch {
        // continue
      }
    }
    await load();
  };

  const visible = notifications
    .filter((n) => n.status !== 'dismissed')
    .filter((n) => filterType === 'all' || toDisplayType(n.type) === filterType)
    .filter((n) => !showUnreadOnly || n.status === 'unread')
    .sort((a, b) => {
      const ta = a.time ? new Date(a.time).getTime() : 0;
      const tb = b.time ? new Date(b.time).getTime() : 0;
      return sortOrder === 'newest' ? tb - ta : ta - tb;
    });

  const unreadCount = getUnreadCount(notifications);

  return (
    <div className={`${embedded ? '' : 'min-h-view'} bg-c3-bg font-c3 text-c3-white`}>
      {!embedded && (
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-arbitium-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-arbitium-accent/5 rounded-full blur-3xl"></div>
      </div>
      )}

      {!embedded && (
      <header className="relative z-10 bg-arbitium-dark/80 backdrop-blur-xl border-b border-arbitium-primary/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 text-t3 hover:text-t1 hover:bg-arbitium-primary/10 rounded-lg transition-colors"
              title="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 text-arbitium-accent" />
              <h1 className="text-xl font-semibold text-t1">Notifications</h1>
              {unreadCount > 0 && (
                <span className="flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 text-sm font-medium text-white bg-red-500 rounded-full">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-2 px-3 py-2 text-sm text-t3 hover:text-t1 hover:bg-arbitium-primary/10 rounded-lg transition-colors"
            >
              <Check className="w-4 h-4" />
              Mark all read
            </button>
            <button
              onClick={clearAllRead}
              className="flex items-center gap-2 px-3 py-2 text-sm text-t3 hover:text-crit hover:bg-crit/10 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Clear read
            </button>
          </div>
        </div>
      </header>
      )}

      <main className={`relative z-10 ${embedded ? 'p-0' : 'p-6'}`}>
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-t3" />
              <span className="text-sm text-t3">Filter:</span>
            </div>
            <div className="flex gap-2">
              {(['all', 'threat', 'remediation', 'tests', 'system', 'user'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    filterType === type
                      ? 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50'
                      : 'text-t3 hover:text-t1 hover:bg-arbitium-primary/10 border border-transparent'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>

            <div className="h-6 border-l border-line mx-2"></div>

            <button
              onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-t3 hover:text-t1 hover:bg-arbitium-primary/10 rounded-lg transition-colors"
            >
              {sortOrder === 'newest' ? <SortDesc className="w-4 h-4" /> : <SortAsc className="w-4 h-4" />}
              {sortOrder === 'newest' ? 'Newest first' : 'Oldest first'}
            </button>

            <button
              onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                showUnreadOnly
                  ? 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50'
                  : 'text-t3 hover:text-t1 hover:bg-arbitium-primary/10 border border-transparent'
              }`}
            >
              <Eye className="w-4 h-4" />
              Unread only
            </button>
          </div>

          <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-arbitium-primary/10 text-sm text-t3 font-medium">
              <div className="col-span-1">Type</div>
              <div className="col-span-5">Details</div>
              <div className="col-span-2">Source</div>
              <div className="col-span-2">Time</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>

            {loading ? (
              <div className="p-8 text-center text-t3">Loading...</div>
            ) : visible.length === 0 ? (
              <div className="p-8 text-center text-t3">No notifications found</div>
            ) : (
              <div className="divide-y divide-arbitium-primary/10">
                {visible.map((n) => {
                  const displayType = toDisplayType(n.type);
                  const isUnread = n.status === 'unread';
                  return (
                    <div
                      key={n._id}
                      className={`grid grid-cols-12 gap-4 px-4 py-4 hover:bg-arbitium-primary/5 transition-colors ${
                        isUnread ? 'bg-arbitium-accent/5' : ''
                      }`}
                    >
                      <div className="col-span-1 flex items-start">
                        <div className={`p-2 rounded-lg ${getTypeColor(displayType)}`}>
                          {getTypeIcon(displayType)}
                        </div>
                      </div>
                      <div className="col-span-5">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-medium ${isUnread ? 'text-t1' : 'text-t2'}`}>
                            {n.title}
                          </h3>
                          {isUnread && (
                            <span className="w-2 h-2 bg-arbitium-accent rounded-full shrink-0"></span>
                          )}
                        </div>
                        <p className="text-sm text-t3">{n.details || '—'}</p>
                      </div>
                      <div className="col-span-2 flex items-center">
                        <span className="text-sm text-t3">{n.source || '—'}</span>
                      </div>
                      <div className="col-span-2 flex items-center">
                        <div>
                          <p className="text-sm text-t3">{formatTime(n.time)}</p>
                          <p className="text-xs text-t3">{formatFullTime(n.time)}</p>
                        </div>
                      </div>
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        {isUnread && (
                          <button
                            onClick={() => markAsRead(n._id)}
                            className="p-2 text-t3 hover:text-done hover:bg-done/10 rounded-lg transition-colors"
                            title="Mark as read"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteNotification(n._id)}
                          className="p-2 text-t3 hover:text-crit hover:bg-crit/10 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-4 text-sm text-t3 text-center">
            Showing {visible.length} of {notifications.filter((n) => n.status !== 'dismissed').length} notifications
          </div>
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
